import { ItemCard, StudentItemCard } from '../types/itemCard';
import { ClassMembersPartitionService } from './classMembersPartitionService';

export class StudentItemCardService {
  private userId: string;
  private classId: string;
  private partitionService: ClassMembersPartitionService;

  constructor(userId: string, classId: string) {
    this.userId = userId;
    this.classId = classId;
    this.partitionService = new ClassMembersPartitionService(userId, classId);
  }

  // 멤버에게 아이템 카드 지급
  async grantItemCardToStudent(
    studentId: string, 
    itemCard: ItemCard,
    options?: {
      acquiredLevel?: number;
    }
  ): Promise<void> {
    try {
      // 고유한 인스턴스 ID 생성 (타임스탬프 + 랜덤 문자열)
      const instanceId = `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

      // 아이템 카드 데이터 생성 (undefined 필드 제외)
      const newItemCard: any = {
        instanceId: instanceId,
        itemCardId: itemCard.itemCardId,
        type: itemCard.type, // passive / active 저장
        acquiredAt: new Date(),
        category: typeof options?.acquiredLevel === 'number'
          ? 'levelup'
          : itemCard.category // levelup / assignment / store
      };

      if (typeof options?.acquiredLevel === 'number') {
        newItemCard.acquiredLevel = options.acquiredLevel;
        newItemCard.isLocked = false;
      }

      // active 타입이고 useCount가 있을 때만 remainingUseCount 추가
      if (itemCard.type === 'active' && itemCard.useCount !== undefined) {
        newItemCard.remainingUseCount = itemCard.useCount;
      }

      await this.partitionService.addStudentItemCardAtomic(studentId, newItemCard);

      console.log(`✅ 멤버 ${studentId}에게 아이템 ${itemCard.name} 지급 완료`);
    } catch (error) {
      console.error(`❌ 멤버 ${studentId}에게 아이템 지급 실패:`, error);
      throw error;
    }
  }

  // 여러 멤버에게 동시에 아이템 카드 지급
  async grantItemCardToMultipleStudents(
    studentIds: string[],
    itemCard: ItemCard
  ): Promise<void> {
    const promises = studentIds.map(studentId => 
      this.grantItemCardToStudent(studentId, itemCard)
    );

    try {
      await Promise.all(promises);
      console.log(`✅ ${studentIds.length}명의 멤버에게 아이템 ${itemCard.name} 지급 완료`);
    } catch (error) {
      console.error('❌ 여러 멤버에게 아이템 지급 실패:', error);
      throw error;
    }
  }

  // 멤버의 보유 아이템 카드 조회
  async getStudentItemCards(studentId: string): Promise<StudentItemCard[]> {
    try {
      const student = await this.partitionService.getMember(studentId);
      if (!student || !(student as any).itemCards) {
        return [];
      }

      return (student as any).itemCards.map((item: any) => ({
        instanceId: item.instanceId,
        itemCardId: item.itemCardId,
        type: item.type, // passive / active
        remainingUseCount: item.remainingUseCount,
        acquiredAt: (item.acquiredAt as any)?.toDate ? (item.acquiredAt as any).toDate() : item.acquiredAt,
        category: item.category || (item.assignmentId ? 'assignment' : 'levelup'), // 기본값 설정
        assignmentId: item.assignmentId,
        isLocked: item.isLocked || false,
        acquiredLevel: item.acquiredLevel
      }));
    } catch (error) {
      console.error(`❌ 멤버 ${studentId}의 아이템 카드 조회 실패:`, error);
      return [];
    }
  }

  // 아이템 카드 사용 (사용 횟수 차감) - instanceId로 특정 카드 인스턴스 식별
  async useItemCard(studentId: string, instanceId: string): Promise<void> {
    try {
      await this.partitionService.updateStudentItemCardsAtomic(studentId, (currentCards) => {
        const itemCards = [...currentCards];
        const itemIndex = itemCards.findIndex(item => item.instanceId === instanceId);

        if (itemIndex === -1) {
          throw new Error('아이템 카드를 찾을 수 없습니다.');
        }

        const item = itemCards[itemIndex];

        if (item.isLocked === true) {
          throw new Error('이 아이템 카드는 레벨다운으로 인해 잠금되어 사용할 수 없습니다.');
        }

        if (item.type === 'passive') {
          console.log(`ℹ️ 이 아이템은 passive 타입입니다 (instanceId: ${instanceId})`);
          return itemCards;
        }

        if (item.remainingUseCount !== undefined) {
          if (item.remainingUseCount > 0) {
            itemCards[itemIndex].remainingUseCount = item.remainingUseCount - 1;
            if (itemCards[itemIndex].remainingUseCount === 0) {
              itemCards.splice(itemIndex, 1);
              console.log(`✅ 멤버 ${studentId}의 active 아이템 인스턴스 ${instanceId} 사용 완료 (사용 횟수 소진)`);
            } else {
              console.log(`✅ 멤버 ${studentId}의 active 아이템 인스턴스 ${instanceId} 사용 완료 (남은 횟수: ${itemCards[itemIndex].remainingUseCount})`);
            }
          } else {
            throw new Error('사용 가능한 횟수가 없습니다.');
          }
          return itemCards;
        }

        throw new Error('active 아이템인데 remainingUseCount가 없습니다.');
      });
    } catch (error) {
      console.error(`❌ 아이템 사용 실패:`, error);
      throw error;
    }
  }
}

